import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { StatusBadge } from "../../components/crm/StatusBadge";
import { Spinner } from "../../components/Spinner";
import { getContact, isApiError, listDeals } from "../../lib/crm-api";
import type { Contact, Deal } from "../../lib/crm-types";
import { formatDate, formatMoney } from "../../lib/format";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

function ContactDetailInner({ id }: { id: string }): ReactNode {
  const { t, lang } = useConfig();
  const { hasPermission } = useSession();
  const canUpdate = hasPermission("crm.contact.update");
  const [contact, setContact] = useState<Contact | null>(null);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const locale = lang === "es" ? "es-ES" : "en-US";

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [row, page] = await Promise.all([
        getContact(id),
        listDeals({ contact_id: id, limit: 50 }),
      ]);
      setContact(row);
      setDeals(page.items);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const fullName =
    contact === null
      ? t("crm.contacts.detail")
      : [contact.first_name, contact.last_name].filter((part) => Boolean(part)).join(" ");

  return (
    <section className="page" aria-labelledby="crm-contact-detail-title">
      <header className="page__header page__header--row">
        <div>
          <h1 id="crm-contact-detail-title">{fullName}</h1>
          {contact !== null ? (
            <p className="muted">
              <StatusBadge status={contact.status} />
            </p>
          ) : null}
        </div>
        {canUpdate && contact !== null ? (
          <Link className="btn btn--ghost" to={`/crm/contacts/${contact.id}/edit`}>
            {t("crm.common.edit")}
          </Link>
        ) : null}
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && contact !== null ? (
        <>
          <dl className="detail-list">
            <div className="detail-list__row">
              <dt>{t("crm.contact.firstName")}</dt>
              <dd>{contact.first_name}</dd>
            </div>
            <div className="detail-list__row">
              <dt>{t("crm.contact.lastName")}</dt>
              <dd>{contact.last_name ?? "—"}</dd>
            </div>
            <div className="detail-list__row">
              <dt>{t("crm.contact.company")}</dt>
              <dd>
                {contact.company_id === null ? (
                  "—"
                ) : (
                  <Link to={`/crm/companies/${contact.company_id}`}>{t("crm.contact.viewCompany")}</Link>
                )}
              </dd>
            </div>
            <div className="detail-list__row">
              <dt>{t("crm.contact.email")}</dt>
              <dd>{contact.email ?? "—"}</dd>
            </div>
            <div className="detail-list__row">
              <dt>{t("crm.contact.phone")}</dt>
              <dd>{contact.phone ?? "—"}</dd>
            </div>
            <div className="detail-list__row">
              <dt>{t("crm.contact.mobile")}</dt>
              <dd>{contact.mobile ?? "—"}</dd>
            </div>
            <div className="detail-list__row">
              <dt>{t("crm.contact.jobTitle")}</dt>
              <dd>{contact.job_title ?? "—"}</dd>
            </div>
          </dl>

          <h2 className="section-title">{t("crm.company.tabs.deals")}</h2>
          {deals.length === 0 ? (
            <p className="empty">{t("crm.deals.empty")}</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <caption className="sr-only">{t("crm.company.tabs.deals")}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t("crm.deal.title")}</th>
                    <th scope="col">{t("crm.deal.value")}</th>
                    <th scope="col">{t("crm.common.status")}</th>
                    <th scope="col">{t("crm.deal.expectedClose")}</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((deal) => (
                    <tr key={deal.id}>
                      <td>{deal.title}</td>
                      <td>{formatMoney(deal.value_minor, deal.currency, locale)}</td>
                      <td>
                        <StatusBadge status={deal.status} />
                      </td>
                      <td>{formatDate(deal.expected_close_date, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

export function ContactDetailPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  if (id === undefined) {
    return null;
  }
  return (
    <RequirePermission permission="crm.contact.read">
      <ContactDetailInner id={id} />
    </RequirePermission>
  );
}
